import React, { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
    Box,
    Typography,
    IconButton,
    Divider,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Tooltip,
    CircularProgress,
    Button,
} from '@mui/material';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ListAltIcon from '@mui/icons-material/ListAlt';
import styled from '@emotion/styled';
import { getNotifications, markNotificationRead, markAllNotificationsRead, deleteNotification } from '../redux/ducks/notification';
import { formatDateTime } from '../utils/dateUtils';
import { resolveNotificationLink, previewOf } from '../utils/navigateUtils';
import PushNotificationToggle from './PushNotificationToggle';
import AIResponseDialog, { AIMarkdown } from './AIResponseDialog';

const REMINDER_WINDOW_MINUTES = 120;

const PanelContainer = styled(Box)({
    width: 380,
    maxHeight: 520,
    display: 'flex',
    flexDirection: 'column',
});

const PanelHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    flexShrink: 0,
}));

const NotificationList = styled(List)({
    overflowY: 'auto',
    flexGrow: 1,
    padding: 0,
});

const NotificationItem = styled(ListItem, {
    shouldForwardProp: (prop) => prop !== 'unread',
})(({ theme, unread }) => ({
    backgroundColor: unread ? theme.palette.action.hover : 'transparent',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
}));

const NotificationRow = styled(ListItemButton)(({ theme }) => ({
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    // Reserve space for the delete button
    paddingRight: theme.spacing(6),
    '&:hover': {
        backgroundColor: theme.palette.action.selected,
    },
}));

const HeaderActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
}));

const UnreadDot = styled(FiberManualRecordIcon)(({ theme }) => ({
    fontSize: theme.typography.size.xxs,
    color: theme.palette.primary.main,
    flexShrink: 0,
    marginTop: 6,
}));

const EmptyState = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
}));

const NotificationBody = styled(Typography)({
    whiteSpace: 'normal',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    overflowWrap: 'anywhere',
    maxHeight: '2.9em',
});

const LoadMoreRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(0.25, 1),
    textAlign: 'center',
    flexShrink: 0,
}));

const ReminderEntry = styled(Box)(({ theme }) => ({
    '& + &': {
        marginTop: theme.spacing(2),
        paddingTop: theme.spacing(2),
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}));

const ReminderMeta = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
}));

const linkActionLabel = (link) => {
    if (link.includes('/task/')) return 'Open task';
    if (link.includes('/project/')) return 'Open project';
    if (link.startsWith('/account/')) return 'Open account';
    return 'Open';
};

const isReminder = (notification) => notification.objectType === 'Reminder';

const keepsDropdownOpen = (event) => event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

const NotificationPanel = ({ onClose }) => {
    const dispatch = useDispatch();
    const { items, unreadCount, pagination } = useSelector(state => state.notifications);
    const [loading, setLoading] = React.useState(false);
    const [loadingMore, setLoadingMore] = React.useState(false);
    const [detail, setDetail] = React.useState(null);
    const [reminders, setReminders] = React.useState([]);
    const reminderRequestRef = useRef(null);

    const load = useCallback(() => {
        setLoading(true);
        dispatch(getNotifications({}, () => setLoading(false)));
    }, [dispatch]);

    useEffect(() => {
        load();
    }, [load]);

    const handleDelete = (e, id) => {
        e.stopPropagation();
        dispatch(deleteNotification(id));
    };

    const handleMarkAll = () => {
        dispatch(markAllNotificationsRead());
    };

    const handleSeeAll = (event) => {
        if (onClose && !keepsDropdownOpen(event)) onClose();
    };

    const handleLoadMore = () => {
        if (pagination.page >= pagination.pages) return;
        setLoadingMore(true);
        dispatch(getNotifications({ page: pagination.page + 1 }, () => setLoadingMore(false)));
    };

    const handleItemClick = (event, notification, href) => {
        if (!notification.read) {
            dispatch(markNotificationRead(notification._id));
        }
        if (isReminder(notification)) {
            setDetail(notification);
            setReminders([notification]);
            reminderRequestRef.current = notification._id;
            const since = new Date(Date.now() - REMINDER_WINDOW_MINUTES * 60000).toISOString();
            dispatch(getNotifications({ since, unreadOnly: true, limit: 100 }, (data) => {
                if (reminderRequestRef.current !== notification._id) return;
                const others = (data?.notifications || []).filter((item) => isReminder(item) && item._id !== notification._id);
                setReminders([notification, ...others].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
            }));
            return;
        }
        if (href) {
            if (onClose && !keepsDropdownOpen(event)) onClose();
            return;
        }
        setDetail(notification);
    };

    const handleDetailClose = () => {
        reminders.filter((reminder) => !reminder.read).forEach((reminder) => dispatch(markNotificationRead(reminder._id)));
        reminderRequestRef.current = null;
        setReminders([]);
        setDetail(null);
    };

    const handleReminderOpen = (event) => {
        handleDetailClose();
        if (onClose && !keepsDropdownOpen(event)) onClose();
    };

    const reminderMode = Boolean(detail) && isReminder(detail);

    const detailRecord = React.useMemo(
        () => (detail && !isReminder(detail) ? { status: 'ready', content: detail.body } : null),
        [detail]
    );

    const detailSubtitle = detail
        ? [detail.triggeringUser?.fullName && `Sent by ${detail.triggeringUser.fullName}`, formatDateTime(detail.createdAt)].filter(Boolean).join(' · ')
        : '';

    const formatTime = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 1) return 'Just now';
        if (diffMin < 60) return `${diffMin}m ago`;
        const diffH = Math.floor(diffMin / 60);
        if (diffH < 24) return `${diffH}h ago`;
        const diffD = Math.floor(diffH / 24);
        return `${diffD}d ago`;
    };

    return (
        <PanelContainer>
            <PanelHeader>
                <Typography variant="subtitle1" fontWeight={600}>
                    Notifications {unreadCount > 0 && `(${unreadCount > 99 ? '99+' : unreadCount})`}
                </Typography>
                <HeaderActions>
                    {unreadCount > 0 && (
                        <Tooltip title="Mark all as read">
                            <IconButton size="small" onClick={handleMarkAll}>
                                <DoneAllIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="See all notifications">
                        <IconButton size="small" component={Link} to="/notifications" onClick={handleSeeAll}>
                            <ListAltIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </HeaderActions>
            </PanelHeader>
            <Divider />
            {loading ? (
                <EmptyState>
                    <CircularProgress size={24} />
                </EmptyState>
            ) : items.length === 0 ? (
                <EmptyState>
                    <Typography variant="body2">No notifications</Typography>
                </EmptyState>
            ) : (
                <>
                    <NotificationList>
                        {items.map((notification) => {
                            const href = isReminder(notification) ? null : resolveNotificationLink(notification);
                            return (
                                <NotificationItem
                                    key={notification._id}
                                    unread={!notification.read}
                                    disablePadding
                                    secondaryAction={
                                        <Tooltip title="Delete">
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                onClick={(e) => handleDelete(e, notification._id)}
                                            >
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    }
                                >
                                    <NotificationRow
                                        {...(href ? { component: Link, to: href } : {})}
                                        onClick={(event) => handleItemClick(event, notification, href)}
                                    >
                                        {!notification.read && <UnreadDot />}
                                        <ListItemText
                                            primary={
                                                <Typography variant="body2" fontWeight={notification.read ? 400 : 600} noWrap>
                                                    {notification.subject}
                                                </Typography>
                                            }
                                            secondary={
                                                <Box component="span">
                                                    <NotificationBody variant="caption" display="block">
                                                        {previewOf(notification.body)}
                                                    </NotificationBody>
                                                    <Typography variant="caption" color="text.disabled">
                                                        {notification.triggeringUser?.fullName} · {formatTime(notification.createdAt)}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </NotificationRow>
                                </NotificationItem>
                            );
                        })}
                    </NotificationList>
                    {pagination.page < pagination.pages && (
                        <LoadMoreRow>
                            <Button
                                size="small"
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                            >
                                {loadingMore ? <CircularProgress size={16} /> : 'Load more'}
                            </Button>
                        </LoadMoreRow>
                    )}
                </>
            )}
            <PushNotificationToggle />
            <AIResponseDialog
                open={Boolean(detail)}
                onClose={handleDetailClose}
                title={reminderMode && reminders.length > 1 ? `Reminders (${reminders.length})` : detail?.subject || 'Notification'}
                subtitle={reminderMode && reminders.length > 1 ? 'Unread from the last 2 hours' : detailSubtitle}
                icon={<NotificationsIcon fontSize="small" />}
                record={detailRecord}
            >
                {reminderMode && reminders.map((reminder) => {
                    const reminderHref = resolveNotificationLink(reminder);
                    return (
                    <ReminderEntry key={reminder._id}>
                        <ReminderMeta>
                            <Typography variant="caption" color="text.secondary">{formatDateTime(reminder.createdAt)}</Typography>
                            {reminderHref && (
                                <Button size="small" component={Link} to={reminderHref} onClick={handleReminderOpen}>{linkActionLabel(reminderHref)}</Button>
                            )}
                        </ReminderMeta>
                        <AIMarkdown>{reminder.body}</AIMarkdown>
                    </ReminderEntry>
                    );
                })}
            </AIResponseDialog>
        </PanelContainer>
    );
};

export default NotificationPanel;
